import { useFeatureFlag } from '@src/hooks';
import { useCustomerUsers } from '@src/hooks/useCustomer';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import { useCustomerMarketAccounts } from '@src/providers';
import { ACTION, EMPTY_ARRAY, EntityAdmin, type ICustomerFIXConnection } from '@talos/kyoko';
import { useMemo } from 'react';
import {
  getCustomerFIXConnectionsColumns,
  getCustomerFixConnectionDrawerOptions,
  useGetCustomerFixConnectionContextMenuItems,
} from './utils';

export function CustomerFIXConnections() {
  const { isAuthorized } = useRoleAuth();
  const customerUsers = useCustomerUsers();
  const { customerMarketAccountsList } = useCustomerMarketAccounts();
  const { allowClientFIXConnectionCreation, showCustomerAccountSelectorForCustomerFIX } = useFeatureFlag();

  const drawerOptions = useMemo(() => {
    if (!customerUsers) {
      return undefined;
    }
    if (showCustomerAccountSelectorForCustomerFIX && !customerMarketAccountsList) {
      return undefined;
    }
    return getCustomerFixConnectionDrawerOptions({
      customerUsers,
      customerMarketAccounts: customerMarketAccountsList ?? EMPTY_ARRAY,
      showCustomerAccountSelector: showCustomerAccountSelectorForCustomerFIX,
    });
  }, [customerUsers, customerMarketAccountsList, showCustomerAccountSelectorForCustomerFIX]);

  const columns = useMemo(
    () => getCustomerFIXConnectionsColumns({ showCustomerAccountSelector: showCustomerAccountSelectorForCustomerFIX }),
    [showCustomerAccountSelectorForCustomerFIX]
  );

  const getCustomContextMenuItems = useGetCustomerFixConnectionContextMenuItems();

  const allowAddingCustomerFIXConnectionFromFeatureFlag = useMemo(
    () => isAuthorized(ACTION.EDIT_DEALER) && allowClientFIXConnectionCreation,
    [isAuthorized, allowClientFIXConnectionCreation]
  );

  const allowAddingCustomerFIXConnectionFromRole = useMemo(
    () => isAuthorized(ACTION.EDIT_CUSTOMER_FIX_CONNECTIONS),
    [isAuthorized]
  );

  return (
    <EntityAdmin<ICustomerFIXConnection>
      title="FIX Connections"
      subtitle="View and manage customer FIX sessions."
      entityName="FIX Connection"
      path="/organization/customer-fix-connections"
      entityIDField="TargetCompID"
      columns={columns}
      drawerOptions={drawerOptions}
      newEntityDefault={() => ({ BeginString: 'FIX.4.4' })}
      allowAddEntity={allowAddingCustomerFIXConnectionFromFeatureFlag || allowAddingCustomerFIXConnectionFromRole}
      allowDeleteEntity={isAuthorized(ACTION.EDIT_CUSTOMER_FIX_CONNECTIONS)}
      getDeletePath={entity => `/organization/customer-fix-connections/${entity.ConnectionID}`}
      persistKey="dealer/customer-fix-connections"
      filterableProperties={EMPTY_ARRAY}
      getCustomContextMenuItems={getCustomContextMenuItems}
      postEntityInArray={true}
    />
  );
}
